import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Minus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { FormDots, HeatBadge, TeamLink } from "@/components/StandingsTable";
import { TeamFollowButton } from "@/components/TeamFollowButton";

function Delta({ v, testid }) {
  if (v == null) return <span className="text-slate-600" data-testid={testid}>—</span>;
  if (v === 0) return <span className="inline-flex items-center text-slate-500" data-testid={testid}><Minus size={11} /></span>;
  return <span data-testid={testid} className={`inline-flex items-center gap-0.5 font-mono-num text-xs font-bold ${v > 0 ? "text-emerald-400" : "text-red-400"}`}>{v > 0 ? <ArrowUp size={11} /> : <ArrowDown size={11} />}{Math.abs(v)}</span>;
}

function Bar({ v, color }) {
  return <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#0B0E14]"><div className="h-full rounded-full" style={{ width: `${v}%`, background: color }} /></div>;
}

export function PowerRankings({ sport }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setD(null);
    api.get(`/power-rankings/${sport}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e)));
  }, [sport]);
  if (err) return <p className="text-sm text-red-400" data-testid="power-error">{err}</p>;
  if (!d) return <div className="glass-card h-64 animate-pulse" data-testid="power-loading" />;
  const th = "px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500";
  return (
    <div className="space-y-3" data-testid="power-rankings">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500" data-testid="power-meta">
        <span className="font-display text-lg font-bold uppercase text-white">{d.league} power rankings · {d.week}</span>
        <span>Blend: record {d.weights.record}% · form {d.weights.form}% · margin {d.weights.margin}% · closing-line value {d.weights.clv}%</span>
        {!d.has_prev_week && <span className="text-slate-600">Week-over-week arrows start next week.</span>}
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead className="border-b border-[#26334D]"><tr>
            <th className={th}>#</th><th className={th}>{d.kind === "rankings" ? "Player" : "Team"}</th><th className={`${th} w-44`}>Power score</th><th className={`${th} text-right`}>vs table</th><th className={`${th} text-right`}>Wk</th><th className={`${th} text-right`}>Record</th><th className={th}>Form</th><th className={`${th} text-right`}>Margin</th><th className={`${th} text-right`}>CLV</th><th className={th}></th>
          </tr></thead>
          <tbody className="divide-y divide-[#26334D]/60">
            {d.rows.map((r) => (
              <tr key={r.team} data-testid="power-row" className="transition-colors hover:bg-[#182030]">
                <td className="font-mono-num px-3 py-2 text-sm text-slate-500">{r.rank}</td>
                <td className="px-3 py-2 text-sm"><span className="flex items-center gap-2 font-semibold text-white"><TeamLink sport={sport} team={r.team} /><HeatBadge heat={r.heat} /></span></td>
                <td className="px-3 py-2"><div className="flex items-center gap-2"><span className="font-mono-num w-10 text-right text-sm font-bold text-amber-400" data-testid="power-score">{r.score.toFixed(1)}</span><Bar v={r.score} color={r.score >= 60 ? "#10B981" : r.score >= 45 ? "#F59E0B" : "#EF4444"} /></div></td>
                <td className="px-3 py-2 text-right"><span className="font-mono-num mr-1 text-xs text-slate-500">#{r.table_rank}</span><Delta v={r.vs_table} testid="power-vs-table" /></td>
                <td className="px-3 py-2 text-right"><Delta v={r.week_change} testid="power-week-change" /></td>
                <td className="font-mono-num px-3 py-2 text-right text-sm text-slate-300">{r.record}</td>
                <td className="px-3 py-2"><FormDots form={r.form} /></td>
                <td className={`font-mono-num px-3 py-2 text-right text-sm ${r.avg_margin > 0 ? "text-emerald-400" : r.avg_margin < 0 ? "text-red-400" : "text-slate-400"}`}>{r.avg_margin > 0 ? "+" : ""}{r.avg_margin}</td>
                <td className={`font-mono-num px-3 py-2 text-right text-sm ${r.clv == null ? "text-slate-600" : r.clv > 0 ? "text-emerald-400" : r.clv < 0 ? "text-red-400" : "text-slate-400"}`} title="Avg points the line moved toward the model's side when backing this team (last 10 days)">{r.clv == null ? "—" : `${r.clv > 0 ? "+" : ""}${r.clv}`}</td>
                <td className="px-2 py-2"><TeamFollowButton sport={sport} slug={r.slug} team={r.team} size="icon" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
